import type { ReactNode } from 'react';
import { WhatsAppButton } from '@/components/product/WhatsAppButton';
import { AppLink } from '@/components/ui/AppLink';
import { buttonClasses } from '@/components/ui/button-classes';
import { Section } from '@/components/ui/Section';
import { useT } from '@/lib/i18n';
import { Reveal } from '@/motion/Reveal';

export interface CtaBandProps {
  title: ReactNode;
  body?: ReactNode;
  /** Label of the enquiry button. Default: the common "Send an enquiry". */
  enquiryLabel?: string;
  /** Pre-fill the contact form with this product (its slug). */
  product?: string;
  tone?: 'bg' | 'surface';
  /** A slim band (a headline and the two buttons, little air): the product page's (D-366). */
  compact?: boolean;
}

/**
 * The closing call to action at the bottom of a page: a headline, one line of copy, the enquiry
 * button (→ `/contact-us#enquiry`, optionally with a product pre-filled) and the WhatsApp button.
 */
export function CtaBand({
  title,
  body,
  enquiryLabel,
  product,
  tone = 'surface',
  compact = false,
}: CtaBandProps) {
  const t = useT();
  return (
    <Section
      tone={tone}
      align="center"
      title={title}
      {...(compact ? { className: 'cta-band--compact retail' } : {})}
    >
      {body ? (
        // justified, last line centred under it, in a column of its own (`.cta-band__body`, D-356)
        <Reveal as="p" className="cta-band__body t-lead text-fg-muted">
          {body}
        </Reveal>
      ) : null}
      <Reveal delay={120} className="cta-band__actions">
        <AppLink
          to={{
            pathname: '/contact-us',
            ...(product ? { search: `?product=${product}` } : {}),
            hash: '#enquiry',
          }}
          className={buttonClasses({ variant: 'primary', size: 'lg' })}
        >
          {enquiryLabel ?? t('cta.enquiry')}
        </AppLink>
        <WhatsAppButton size="lg" />
      </Reveal>
    </Section>
  );
}
